import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { getDecryptedToken } from "../../utils/crypto/cryptoUtils";
import subscriptionService, {
  SubscriptionData,
} from "../../services/subscription/subscriptionService";
import payhereService from "../../services/payhere/payhereService";
import { toast } from "react-toastify";
import "./PaymentView.scss";
import paymentService from "../../services/payment/paymentService";
import type { Invoice } from "../../services/payment/paymentService";

const PaymentView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedData = location.state as { subscriptionData?: SubscriptionData };

  const [subscriptionData, setSubscriptionData] =
    useState<SubscriptionData | null>(passedData?.subscriptionData || null);
  const [selectedPackage, setSelectedPackage] = useState("1");
  const [fromDate, setFromDate] = useState("");
  const [validTillDate, setValidTillDate] = useState("");
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [loading, setLoading] = useState(!passedData?.subscriptionData);
  const [processing, setProcessing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  // Get clinic/shop details from token
  const clinicName =
    getDecryptedToken("martName") ||
    getDecryptedToken("companyName") ||
    "Your Business";
  const ownerName = getDecryptedToken("name") || "";
  //   const currencySymbol = getDecryptedToken("currency") || "Rs.";

  // Fetch subscription data if not passed from PosTop
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!subscriptionData) {
        try {
          setLoading(true);
          // Get companyId from token and use branch token API
          const companyId = getDecryptedToken("companyId");

          if (!companyId) {
            throw new Error("Company ID not found in token");
          }

          // Use branch token to fetch subscription by companyId
          const data = await subscriptionService.getCurrentSubscription();
          setSubscriptionData(data);

          // Fetch invoices using branch token
          if (data.subscription?.companyId) {
            const invoiceData = await paymentService.getInvoicesFromBranch(
              data.subscription.companyId.toString(),
            );
            setInvoices(invoiceData);
          }
        } catch (error) {
          console.error("Error fetching subscription:", error);
        } finally {
          setLoading(false);
        }
      } else if (subscriptionData?.subscription?.companyId) {
        // Fetch invoices for passed subscription
        const invoiceData = await paymentService.getInvoicesFromBranch(
          subscriptionData.subscription.companyId.toString(),
        );
        setInvoices(invoiceData);
      }
    };
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update countdown timer every second
  useEffect(() => {
    if (subscriptionData?.subscription) {
      const updateTimer = () => {
        // Use paidUntilDate if available (shows countdown to payment due date)
        // Otherwise, use endDate (subscription expiration)
        const targetDate = subscriptionData.subscription!.paidUntilDate
          ? new Date(subscriptionData.subscription!.paidUntilDate)
          : new Date(subscriptionData.subscription!.endDate);

        const timeRemaining =
          subscriptionService.calculateTimeRemaining(targetDate);
        setTimeLeft(timeRemaining);
      };

      updateTimer();
      const timer = setInterval(updateTimer, 1000);
      return () => clearInterval(timer);
    }
  }, [subscriptionData]);

  // Calculate dates based on selected package
  useEffect(() => {
    const today = new Date();
    const months = parseInt(selectedPackage);

    setFromDate(formatDate(today));

    const endDate = new Date(today);
    endDate.setMonth(endDate.getMonth() + months);
    setValidTillDate(formatDate(endDate));
  }, [selectedPackage]);

  const formatDate = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const calculateTotalAmount = () => {
    if (!subscriptionData?.subscription?.Plan) return 0;
    const monthlyAmount = subscriptionData.subscription.Plan.amount;
    const months = parseInt(selectedPackage);
    return monthlyAmount * months;
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // ⚠️ TEST FUNCTION - DELETE IN PRODUCTION!
  const handleTestPayment = async () => {
    if (!subscriptionData?.subscription) {
      toast.error("No active subscription found");
      return;
    }

    if (processing) {
      return;
    }

    try {
      setProcessing(true);

      const totalAmount = calculateTotalAmount();
      const companyId = subscriptionData.subscription.companyId;
      const subscriptionId = subscriptionData.subscription.id;

      toast.info("🧪 Creating payment order...");

      // First create the payment order
      const order = await paymentService.createPaymentOrder({
        companyId,
        subscriptionId,
        amount: totalAmount,
        currency: currency,
        months: parseInt(selectedPackage),
      });

      toast.info("🧪 Simulating test payment...");

      // Call test webhook with the created order
      await paymentService.testPaymentWebhook({
        orderId: order.orderId,
        companyId,
        subscriptionId,
        amount: totalAmount,
        currency: currency,
        months: parseInt(selectedPackage),
      });

      toast.success("✅ Test payment successful! Check console for details.");

      // Refresh subscription data
      setTimeout(async () => {
        const updatedData = await subscriptionService.getCurrentSubscription();
        setSubscriptionData(updatedData);
        toast.success("Subscription updated!");
      }, 1000);
    } catch (error: any) {
      console.error("Test payment error:", error);
      toast.error(error.message || "Test payment failed");
    } finally {
      setProcessing(false);
    }
  };

  const handlePayNow = async () => {
    if (!subscriptionData?.subscription) {
      toast.error("No active subscription found");
      return;
    }

    if (processing) {
      return; // Prevent double submission
    }

    try {
      setProcessing(true);

      // Check if PayHere is loaded
      if (!payhereService.isPayHereLoaded()) {
        toast.error("Payment gateway not loaded. Please refresh the page.");
        setProcessing(false);
        return;
      }

      const totalAmount = calculateTotalAmount();
      const companyId = subscriptionData.subscription.companyId;
      const subscriptionId = subscriptionData.subscription.id;

      // Create payment order on backend
      toast.info("Preparing payment...");
      const order = await paymentService.createPaymentOrder({
        companyId,
        subscriptionId,
        amount: totalAmount,
        currency: currency,
        months: parseInt(selectedPackage),
      });

      console.log("Payment order created:", order);

      // Get customer details
      const customerName = getDecryptedToken("name") || "Customer";
      const customerEmail = getDecryptedToken("email") || "";
      const customerPhone = getDecryptedToken("phone") || "";
      const nameParts = customerName.split(" ");

      // Initiate PayHere payment
      toast.info("Opening payment gateway...");

      await payhereService.initiatePayment(
        order.orderId,
        order.amount,
        order.currency,
        `${order.planName} - ${selectedPackage} Month(s)`,
        order.hash, // Pass hash from backend
        order.merchantId, // Pass merchant ID from backend
        companyId, // Pass company ID for webhook
        subscriptionId, // Pass subscription ID for webhook
        parseInt(selectedPackage), // Pass months for subscription extension
        {
          firstName: nameParts[0] || "",
          lastName: nameParts.slice(1).join(" ") || "",
          email: customerEmail,
          phone: customerPhone,
          address: "",
          city: "",
          country: "Sri Lanka",
        },
      );

      // Payment completed successfully
      toast.success("Payment completed successfully!");

      // Wait a moment for webhook to process
      setTimeout(async () => {
        // Refresh subscription data using branch token
        const updatedData = await subscriptionService.getCurrentSubscription();
        setSubscriptionData(updatedData);
        toast.success("Subscription updated successfully!");

        // Navigate to POS after 1 second
        setTimeout(() => {
          navigate("/pos");
        }, 1000);
      }, 3000);
    } catch (error: any) {
      console.error("Payment error:", error);

      if (error.message?.includes("dismissed")) {
        toast.warning("Payment cancelled");
      } else {
        toast.error(error.message || "Payment failed. Please try again.");
      }
    } finally {
      setProcessing(false);
    }
  };

  // Get package options based on billing type
  const getPackageOptions = () => {
    if (!subscriptionData?.subscription?.Plan) {
      return [
        { value: "1", label: "1 Month", months: 1 },
        { value: "3", label: "3 Months", months: 3 },
        { value: "6", label: "6 Months", months: 6 },
        { value: "12", label: "12 Months", months: 12 },
      ];
    }

    const { billingType, customDays } = subscriptionData.subscription.Plan;

    switch (billingType) {
      case "daily":
        return [
          { value: "7", label: "7 Days", months: 0.23 },
          { value: "15", label: "15 Days", months: 0.5 },
          { value: "30", label: "30 Days", months: 1 },
        ];
      case "weekly":
        return [
          { value: "1", label: "1 Week", months: 0.23 },
          { value: "4", label: "4 Weeks", months: 1 },
          { value: "12", label: "12 Weeks", months: 3 },
        ];
      case "monthly":
        return [
          { value: "1", label: "1 Month", months: 1 },
          { value: "3", label: "3 Months", months: 3 },
          { value: "6", label: "6 Months", months: 6 },
          { value: "12", label: "12 Months", months: 12 },
        ];
      case "yearly":
        return [
          { value: "1", label: "1 Year", months: 12 },
          { value: "2", label: "2 Years", months: 24 },
          { value: "3", label: "3 Years", months: 36 },
        ];
      case "custom":
        const days = customDays || 30;
        return [
          { value: "1", label: `${days} Days`, months: days / 30 },
          { value: "2", label: `${days * 2} Days`, months: (days * 2) / 30 },
          { value: "3", label: `${days * 3} Days`, months: (days * 3) / 30 },
        ];
      default:
        return [
          { value: "1", label: "1 Month", months: 1 },
          { value: "3", label: "3 Months", months: 3 },
          { value: "6", label: "6 Months", months: 6 },
        ];
    }
  };

  if (loading) {
    return (
      <div className="payment-view-container">
        <div className="loading-state">Loading subscription data...</div>
      </div>
    );
  }

  if (!subscriptionData?.subscription) {
    return (
      <div className="payment-view-container">
        <div className="error-state">
          <h3>No Active Subscription</h3>
          <p>Please contact support to set up your subscription.</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const monthlyRental = subscriptionData.subscription.Plan.amount;
  const currency = subscriptionData.subscription.Plan.currency;
  const packages = getPackageOptions();

  return (
    <div className="payment-view-container">
      <div className="payment-modal-wrapper">
        <div className="payment-modal">
          <div className="modal-header">
            <BusinessIcon className="header-icon" />
            <div className="header-text">
              <h2>Payment Details</h2>
              <p>Complete Your Subscription</p>
            </div>
          </div>

          <div className="modal-body">
            {/* Payment Status Section */}
            {subscriptionData.subscription?.lastPaymentDate && (
              <div className="payment-status-section">
                {subscriptionData.subscription.isPaidPeriod ? (
                  <div className="status-card paid">
                    <div className="status-icon">✓</div>
                    <div className="status-info">
                      <h3>Payment Status: PAID</h3>
                      <p>
                        Valid for {subscriptionData.subscription.daysUntilDue}{" "}
                        more day
                        {subscriptionData.subscription.daysUntilDue !== 1
                          ? "s"
                          : ""}
                      </p>
                      <p className="paid-until">
                        Paid until:{" "}
                        {new Date(
                          subscriptionData.subscription.paidUntilDate!,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="status-card unpaid">
                    <div className="status-icon">!</div>
                    <div className="status-info">
                      <h3>Payment Required</h3>
                      <p>
                        {subscriptionData.subscription.daysSinceLastPayment} day
                        {subscriptionData.subscription.daysSinceLastPayment !==
                        1
                          ? "s"
                          : ""}{" "}
                        since last payment
                      </p>
                      <p className="last-payment">
                        Last paid:{" "}
                        {new Date(
                          subscriptionData.subscription.lastPaymentDate,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="clinic-info">
              <BusinessIcon className="clinic-icon" />
              <div className="clinic-details">
                <span className="label">SHOP NAME</span>
                <h3>{clinicName}</h3>
                <p className="owner-name">{ownerName}</p>
              </div>
            </div>

            <div className="rental-amount">
              <span className="label">Monthly Rental</span>
              <h2 className="amount">
                {currency} {monthlyRental.toLocaleString()}
              </h2>
            </div>

            <div className="package-selection">
              <label className="section-label">
                Select Your Preferred Package
              </label>
              <div className="select-wrapper">
                <AccessTimeIcon className="select-icon" />
                <select
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  className="package-select"
                >
                  {packages.map((pkg) => (
                    <option key={pkg.value} value={pkg.value}>
                      {pkg.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="total-amount">
              <span className="label">Total Amount</span>
              <h2 className="amount">
                {currency} {calculateTotalAmount().toLocaleString()}
              </h2>
            </div>

            <div className="date-info">
              <div className="date-item">
                <span className="date-label">From:</span>
                <span className="date-value">{fromDate}</span>
              </div>
              <div className="date-item">
                <span className="date-label">Valid Till:</span>
                <span className="date-value">{validTillDate}</span>
              </div>
            </div>

            {/* Countdown Timer */}
            {subscriptionData.subscription?.isPaidPeriod && (
              <div className="countdown-timer">
                <AccessTimeIcon className="timer-icon" />
                <div className="timer-display">
                  <div className="time-unit">
                    <span className="time-value">{timeLeft.days}</span>
                    <span className="time-label">Days</span>
                  </div>
                  <div className="time-separator">:</div>
                  <div className="time-unit">
                    <span className="time-value">
                      {String(timeLeft.hours).padStart(2, "0")}
                    </span>
                    <span className="time-label">Hours</span>
                  </div>
                  <div className="time-separator">:</div>
                  <div className="time-unit">
                    <span className="time-value">
                      {String(timeLeft.minutes).padStart(2, "0")}
                    </span>
                    <span className="time-label">Minutes</span>
                  </div>
                  <div className="time-separator">:</div>
                  <div className="time-unit">
                    <span className="time-value">
                      {String(timeLeft.seconds).padStart(2, "0")}
                    </span>
                    <span className="time-label">Seconds</span>
                  </div>
                </div>
              </div>
            )}

            {/* Payment History */}
            {invoices.length > 0 && (
              <div className="payment-history">
                <div
                  className="history-header"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <h4>Payment History ({invoices.length})</h4>
                  <span className={`toggle-icon ${showHistory ? "open" : ""}`}>
                    ▼
                  </span>
                </div>
                {showHistory && (
                  <div className="history-list">
                    {invoices.slice(0, 5).map((invoice) => (
                      <div
                        key={invoice.id}
                        className={`history-item ${invoice.status}`}
                      >
                        <div className="invoice-info">
                          <span className="invoice-number">
                            {invoice.invoiceNumber}
                          </span>
                          <span className="invoice-period">
                            {new Date(
                              invoice.billingPeriodStart,
                            ).toLocaleDateString()}{" "}
                            -{" "}
                            {new Date(
                              invoice.billingPeriodEnd,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="invoice-details">
                          <span className="invoice-amount">
                            {invoice.currency} {invoice.amount.toLocaleString()}
                          </span>
                          <span className={`invoice-status ${invoice.status}`}>
                            {invoice.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button className="cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              {/* ⚠️ TEST BUTTON - DELETE IN PRODUCTION! */}
              {process.env.NODE_ENV === "development" && (
                <button
                  className="test-btn"
                  onClick={handleTestPayment}
                  disabled={processing}
                  title="Test payment simulation (status_code = 2)"
                >
                  {processing ? "Testing..." : "🧪 Test Payment"}
                </button>
              )}
              <button
                className={`pay-now-btn ${
                  subscriptionData.subscription?.isPaidPeriod
                    ? "renew"
                    : "urgent"
                }`}
                onClick={handlePayNow}
                disabled={processing}
              >
                {processing ? (
                  "Processing..."
                ) : subscriptionData.subscription?.isPaidPeriod ? (
                  <>Extend Subscription</>
                ) : (
                  <>
                    Pay Now - {calculateTotalAmount().toLocaleString()}{" "}
                    {currency}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentView;
